import { IStackItemStyles, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { StatBox } from 'components/Integrations/IntegrationRenderHelpers';
import { HoverCallout } from 'components/Utils/HoverCallout';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AuditStatus } from 'models/internalAudit';
import { InternalAuditSchedule, InternalAuditScheduleOccurrence } from 'models/internalAuditTemplate';
import { TaskColors } from 'models/tasks/task';
import { useContext } from 'react';
import { toLocaleDateShortNoYear } from 'utils/datetime';
import { TaskViewScheduleLevel } from '../../Tasks/View/TasksView';
import { AuditPeriodCallout } from './AuditPeriodCallout';

interface IAuditPeriodSummaryProps {
  schedule: InternalAuditSchedule;
  occurrences: InternalAuditScheduleOccurrence[];
  calculatedDates: Date[];
  level: TaskViewScheduleLevel;
  periodStart: Date;
  onOpenAudit?: (occurrence: InternalAuditScheduleOccurrence) => void;
}

// One period cell of the audit row in the annual plan. Actual occurrences render as solid,
// clickable chips (consecutive periods form the start-to-end bar); calculated future occurrences
// render as outlined ghost chips that cannot be clicked.
export const AuditPeriodSummary = (props: IAuditPeriodSummaryProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const chipWidth = props.level === TaskViewScheduleLevel.Month ? 40 : 70;

  // the same chip treatment as a task in the plan: the neutral colour while the audit is running,
  // the completed (green) one once it is published
  const getOccurrenceStyles = (occurrence: InternalAuditScheduleOccurrence): IStackItemStyles => {
    const isPublished = occurrence.status >= AuditStatus.Published;

    return {
      root: {
        background: appContext.useDarkMode
          ? isPublished
            ? TaskColors.DarkCompleted
            : TaskColors.DarkNormal
          : isPublished
            ? TaskColors.Completed
            : TaskColors.Normal,
        color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
        height: 22,
        width: chipWidth,
        borderRadius: 4,
        cursor: props.onOpenAudit ? 'pointer' : 'default',
      },
    };
  };

  const getGhostStyles = (): IStackItemStyles => {
    return {
      root: {
        background: 'transparent',
        color: theme.palette.themePrimary,
        height: 22,
        width: chipWidth,
        borderRadius: 4,
        border: `1px dashed ${appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themePrimary}`,
        cursor: 'default',
      },
    };
  };

  const getChipText = (date: Date | undefined): string => {
    if (!date) return '';
    if (props.level === TaskViewScheduleLevel.Month) return date.getDate().toString();

    return toLocaleDateShortNoYear(date);
  };

  // An audit that runs over several periods only carries its date in the period it starts in; the
  // later cells are plain segments of the same bar.
  const getOccurrenceChipText = (occurrence: InternalAuditScheduleOccurrence): string => {
    if (!occurrence.startDate || occurrence.startDate < props.periodStart) return '';

    return getChipText(occurrence.startDate);
  };

  if (props.occurrences.length === 0 && props.calculatedDates.length === 0) return null;

  return (
    <IPStack tokens={globalStackTokensGapExtraSmall}>
      {props.occurrences.map((occurrence) => (
        <IPStack.Item key={`o-${occurrence.internalAuditId}`}>
          <HoverCallout
            openDelayMs={200}
            closeDelayMs={500}
            calloutLeaveBehavior="delay"
            renderContent={() => (
              <AuditPeriodCallout schedule={props.schedule} occurrence={occurrence} onOpenAudit={props.onOpenAudit} />
            )}
          >
            <StatBox
              text={getOccurrenceChipText(occurrence)}
              styles={getOccurrenceStyles(occurrence)}
              onClick={props.onOpenAudit ? () => props.onOpenAudit?.(occurrence) : undefined}
            />
          </HoverCallout>
        </IPStack.Item>
      ))}
      {props.calculatedDates.map((date) => (
        <IPStack.Item key={`c-${date.getTime()}`}>
          <HoverCallout
            openDelayMs={200}
            closeDelayMs={500}
            calloutLeaveBehavior="delay"
            renderContent={() => <AuditPeriodCallout schedule={props.schedule} calculatedDate={date} />}
          >
            <StatBox text={getChipText(date)} styles={getGhostStyles()} />
          </HoverCallout>
        </IPStack.Item>
      ))}
    </IPStack>
  );
};
